import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_regular,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Applied to `BaseFlyout` itself (the class lands on the portal, so descendant
 * selectors reach the panel).
 *
 * Only the width is overridden. The header band, its raised white surface and
 * the tinted body are all `BaseFlyout`'s own treatment now that the shell
 * passes `title` — the drawers no longer flatten the panel to a single white
 * surface. The deliverables put every settings drawer on a fixed 640px and
 * call that width out as *not* an implementer's choice, while BaseFlyout's
 * `preferredWidth` is spacing(145) = 580px shared by every other flyout, so
 * widen here rather than moving the primitive.
 */
export const flyoutStyle = css`
  &.SettingsDrawerShell {
    .BaseFlyout-Content {
      width: min(${spacing(160)}, 100vw);
    }
  }
`

export default css`
  display: flex;
  flex-direction: column;

  /* Card rhythm: white cards stacked in the tinted body. Today every drawer
     has exactly one card — card count follows real content groupings, and
     nothing is split just to echo the multi-card reference. */
  gap: ${spacing(6)};

  .SettingsDrawerShell-card {
    background-color: ${base_colorRaisedSurface};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${cornerRadius};
    padding: ${spacing(5)} ${spacing(6)};
  }

  /*
   * Lead copy. The header band is title-only (its reference description layer
   * is hidden), so each drawer's lead sits at the top of the card instead.
   */
  .SettingsDrawerShell-lead {
    margin: 0 0 ${spacing(3)};
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  /* A lead that needs more than one paragraph passes them through; they keep
     the lead's own type and sit on the same rhythm. */
  .SettingsDrawerShell-lead > p {
    margin: 0 0 ${spacing(2)};

    &:last-child {
      margin-bottom: 0;
    }
  }

  .SettingsDrawerShell-section {
    border-top: 1px solid ${base_colorContentBorder};
    margin-top: ${spacing(4)};
    padding-top: ${spacing(4)};
  }

  /* Closing info note. The glyph is decorative — the sentence beside it
     carries the meaning. */
  .SettingsDrawerShell-footnote {
    display: flex;
    align-items: flex-start;
    gap: ${spacing(2)};
    margin: ${spacing(4)} 0 0;
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SettingsDrawerShell-footnote > svg {
    flex: 0 0 auto;
    margin-top: ${spacing(0.25)};
  }

  /* SvgTooltip defaults its fill to the CTA colour; this decorative glyph
     inherits the note's own secondary grey instead, matching the design's
     currentColor glyph. A presentation attribute loses to any CSS rule, so
     this wins over the inline fill. */
  .SettingsDrawerShell-footnote > svg g {
    fill: currentColor;
  }

  /*
   * The primary action flows after the last card — it is NOT pinned to the
   * viewport bottom, so it simply moves down as the form grows. The card gap
   * above plus this padding is the reference's 40px between the last card and
   * the action.
   */
  .SettingsDrawerShell-action {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${spacing(3)};
    padding-top: ${spacing(4)};
  }

  /*
   * A rail whose content is portalled in (see SpendLimitTile) is legitimately
   * empty whenever there is nothing to submit — the pending/applied state. The
   * rail must then contribute no spacing at all, or the drawer shows ~40px of
   * dead space under its last card.
   */
  .SettingsDrawerShell-action:has(> *:empty:only-child) {
    display: none;
  }
`
